import loadable from "@loadable/component";
import React, { ComponentType, CSSProperties, SVGAttributes, useCallback, useMemo } from "react";
import { IconBaseProps } from "react-icons/lib/iconBase";
import { IconPack } from "./IconPack.enum";
import { FormGroup } from "react-bootstrap-3/es";
import styled from "styled-components";
import Select from "react-select";
import SearchableSelector from "../../../witadmin/CRUD/Common/SearchableSelector";
import iconList from "../../../witadmin/CRUD/Promotions/iconList";
import * as Icons from "react-icons/md";

const iconComponents: Record<IconPack, () => Promise<Record<string, unknown>>> = {
  [IconPack.ANT_DESIGN_ICONS]: () => import("react-icons/ai"),
  [IconPack.BOOTSTRAP_ICONS]: () => import("react-icons/bs"),
  [IconPack.BOXICONS]: () => import("react-icons/bi"),
  [IconPack.CIRCUM_ICONS]: () => import("react-icons/ci"),
  [IconPack.CSS_GG]: () => import("react-icons/cg"),
  [IconPack.DEVICONS]: () => import("react-icons/di"),
  [IconPack.FEATHER]: () => import("react-icons/fi"),
  [IconPack.FLAT_COLOR_ICONS]: () => import("react-icons/fc"),
  [IconPack.FONT_AWESOME_5]: () => import("react-icons/fa"),
  [IconPack.FONT_AWESOME_6]: () => import("react-icons/fa6"),
  [IconPack.GAME_ICONS]: () => import("react-icons/gi"),
  [IconPack.GITHUB_OCTICONS_ICONS]: () => import("react-icons/go"),
  [IconPack.GROMMET_ICONS]: () => import("react-icons/gr"),
  [IconPack.HEROICONS]: () => import("react-icons/hi"),
  [IconPack.HEROICONS_2]: () => import("react-icons/hi2"),
  [IconPack.ICOMOON_FREE]: () => import("react-icons/im"),
  [IconPack.ICONS8_LINE_AWESOME]: () => import("react-icons/lia"),
  [IconPack.IONICONS_4]: () => import("react-icons/io"),
  [IconPack.IONICONS_5]: () => import("react-icons/io5"),
  [IconPack.LUCIDE]: () => import("react-icons/lu"),
  [IconPack.MATERIAL_DESIGN_ICONS]: () => import("react-icons/md"),
  [IconPack.PHOSPHOR_ICONS]: () => import("react-icons/pi"),
  [IconPack.RADIX_ICONS]: () => import("react-icons/rx"),
  [IconPack.REMIX_ICON]: () => import("react-icons/ri"),
  [IconPack.SIMPLE_ICONS]: () => import("react-icons/si"),
  [IconPack.SIMPLE_LINE_ICONS]: () => import("react-icons/sl"),
  [IconPack.TABLER_ICONS]: () => import("react-icons/tb"),
  [IconPack.THEMIFY_ICONS]: () => import("react-icons/tfi"),
  [IconPack.TYPICONS]: () => import("react-icons/ti"),
  [IconPack.VS_CODE_ICONS]: () => import("react-icons/vsc"),
  [IconPack.WEATHER_ICONS]: () => import("react-icons/wi"),
};

const StyledSelect = styled(Select)`
  padding: 0 !important;
`;

function OptionLabelRenderer({
  value,
  name,
  elements,
  iconPack,
  iconBaseProps,
}: {
  value: string;
  name: string;
  elements: Record<string, ComponentType<IconBaseProps>>;
  iconPack: IconPack;
  iconBaseProps: IconBaseProps;
}) {
  if (iconPack === IconPack.MATERIAL_DESIGN_ICONS) {
    const IconComponent = Icons[value];
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ marginRight: "5px" }}>
          {IconComponent ? <IconComponent style={{ width: "24px", height: "24px" }} /> : null}
        </span>
        <span style={{ fontSize: 14 }}>{name}</span>
      </div>
    );
  }
  const Icon = elements[name];
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span style={{ fontSize: 6, marginRight: "5px" }}>
        <Icon {...iconBaseProps} />
      </span>
      <span style={{ fontSize: 14 }}>{name}</span>
    </div>
  );
}

export interface SelectorOption {
  value: string;
  name: string;
}
export interface IconSelectorProps {
  elements: Record<string, ComponentType<IconBaseProps>>;
  iconPack: IconPack;
  value: string;
  hasErrors: boolean;
  onChange: ({ value, field }: { value: string; field: any }) => void;
  field: any;
  disabled: boolean;
  iconBaseProps: IconBaseProps;
}

function IconSelector({
  field,
  value,
  elements,
  disabled,
  onChange,
  hasErrors,
  iconPack,
  iconBaseProps,
}: IconSelectorProps) {
  const { fieldName, placeholder } = field;

  const options = useMemo(() => {
    return Object.keys(elements).map(key => ({ name: key, value: `${iconPack}/${key}` }));
  }, [elements, iconPack]);
  const selectedValue: SelectorOption[] = useMemo(() => options.filter(it => it.value === value), [
    options,
    value,
  ]);
  const optionsLabelRenderer = useCallback(
    ({ value, name }: { value: string; name: string }) =>
      OptionLabelRenderer({ value, name, elements, iconPack, iconBaseProps }),
    [elements, iconBaseProps, iconPack]
  );
  const handleOnchange = useCallback(
    (selectorOption: SelectorOption) => {
      const option: SelectorOption | undefined = options.find(
        it => it.value === selectorOption.value
      );
      if (!option) {
        throw new Error("Invalid option");
      }
      onChange({ value: option.value, field: fieldName });
    },
    [fieldName, onChange, options]
  );

  return (
    <div className={`InputGroup ${field.className}`}>
      <FormGroup>
        <StyledSelect
          formatOptionLabel={optionsLabelRenderer}
          isSearchable={true}
          isClearable={true}
          options={options}
          onChange={handleOnchange}
          value={selectedValue}
          placeholder={placeholder}
          isDisabled={disabled}
          className={`form-control ${hasErrors && "parsley-error"}`}
        />
      </FormGroup>
    </div>
  );
}
interface DynamicIconProps {
  iconPack: IconPack;
  color?: string;
  size?: string;
  className?: string;
  style?: CSSProperties;
  attr?: SVGAttributes<SVGElement>;
  value: string;
  hasErrors: boolean;
  onChange: ({ value, field }: { value: string; field: any }) => void;
  field: any;
  disabled: boolean;
}

export default function DynamicIconList(props: DynamicIconProps) {
  const { value, hasErrors, onChange, field, disabled } = props;
  const iconPack = field.iconPack;
  const materialMutatedField = useMemo(() => {
    return {
      ...field,
      options: iconList,
      optionLabelRenderer: ({ value, name }) => {
        const IconComponent = Icons[value];
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ fontSize: 6, marginRight: "5px" }}>
              {IconComponent ? <IconComponent style={{ width: "24px", height: "24px" }} /> : null}
            </span>
            <span style={{ fontSize: 14 }}>{name}</span>
          </div>
        );
      },
    };
  }, [field]);

  if (iconPack === IconPack.MATERIAL_DESIGN_ICONS) {
    return (
      <SearchableSelector
        value={value}
        field={materialMutatedField}
        disabled={disabled}
        onChange={onChange}
        hasErrors={hasErrors}
      />
    );
  }
  const iconBaseProps = field.iconBaseProps ?? {};
  const iconComponentImport = iconComponents[iconPack];
  if (iconComponentImport) {
    const IconList = loadable(() => iconComponentImport(), {
      resolveComponent: icons => {
        const elements: Record<string, ComponentType<IconBaseProps>> = Object.fromEntries(
          Object.keys(icons).map((icon, index) => [
            icon,
            icons[icon] as ComponentType<IconBaseProps>,
          ])
        );
        return () => (
          <IconSelector
            elements={elements}
            iconPack={iconPack}
            value={value}
            field={field}
            disabled={disabled}
            onChange={onChange}
            hasErrors={hasErrors}
            iconBaseProps={iconBaseProps}
          />
        );
      },
    });

    return (
      <div>
        <IconList />
      </div>
    );
  }

  return <></>;
}
